import { useEffect } from 'react';
import { motion } from 'framer-motion';
import CheckInHeader from '../../components/check-in/CheckInHeader';
import hopefulCloudAsset from '../../../assets/icons/check-in/hopeful-cloud-emo.svg';
import { containerVariants, itemVariants } from '../../lib/animations';
import { useAnalytics } from '../../contexts/AnalyticsContext';

// Confetti particles extracted from Figma (Screen 12, node 2213:12269).
// Positions are percentages of the 430×932 Figma frame so they scale responsively.
const FRAME_W = 430;
const FRAME_H = 932;

const confettiParticles = [
  { left: 79, top: 301, w: 3, h: 8, rot: 51.5, color: '#DBF7F8' },
  { left: 138, top: 299, w: 3, h: 8, rot: -41.3, color: '#DBF7F8' },
  { left: 55, top: 246, w: 3, h: 8, rot: 28.7, color: '#5CA02B' },
  { left: 33, top: 491, w: 3, h: 8, rot: -32.9, color: '#5CA02B' },
  { left: 394, top: 436, w: 4.4, h: 5.6, rot: 28.7, color: '#5CA02B' },
  { left: 364, top: 414, w: 5.4, h: 8, rot: -41.3, color: '#DBF7F8' },
  { left: 202, top: 261, w: 3, h: 8, rot: 9.7, color: '#FF9EB8' },
  { left: 277, top: 286, w: 3, h: 8, rot: -41.3, color: '#DBF7F8' },
  { left: 296, top: 492, w: 3, h: 8, rot: -41.3, color: '#DBF7F8' },
  { left: 369, top: 344, w: 3, h: 8, rot: -39.7, color: '#DBF7F8' },
  { left: 144, top: 508, w: 5.5, h: 8, rot: -39.7, color: '#DBF7F8' },
  { left: 269, top: 243, w: 7, h: 8, rot: 10.1, color: '#DBF7F8' },
  { left: 357, top: 506, w: 3, h: 8, rot: 10.1, color: '#DBF7F8' },
  { left: 55, top: 458, w: 3, h: 8, rot: 10.1, color: '#DBF7F8' },
  { left: 171, top: 233, w: 4.5, h: 8, rot: 106.7, color: '#DBF7F8' },
  { left: 317, top: 334, w: 5.7, h: 8, rot: -176.8, color: '#FF9EB8' },
  { left: 103, top: 246, w: 6.9, h: 8, rot: -176.8, color: '#6F6FD4' },
  { left: 106, top: 510, w: 3, h: 8, rot: -102, color: '#6F6FD4' },
  { left: 68, top: 340, w: 6, h: 8, rot: -102, color: '#FFA100' },
  { left: 359, top: 238, w: 4.1, h: 5.3, rot: -102, color: '#FFA100' },
  { left: 42, top: 298, w: 4.3, h: 6, rot: -102, color: '#D32640' },
  { left: 317, top: 219, w: 3, h: 5.3, rot: -18.7, color: '#D32640' },
  { left: 331, top: 472, w: 4.5, h: 5.7, rot: -102, color: '#FFA100' },
  { left: 229, top: 291, w: 3, h: 5.3, rot: 61.8, color: '#FFA100' },
  { left: 137, top: 229, w: 3, h: 5.3, rot: -57.8, color: '#FFA100' },
  { left: 185, top: 525, w: 3, h: 5.3, rot: -1.1, color: '#FFA100' },
  { left: 246, top: 217, w: 3, h: 8, rot: -102, color: '#6F6FD4' },
  { left: 366, top: 284, w: 4.2, h: 8, rot: -102.2, color: '#6F6FD4' },
  { left: 368, top: 470, w: 3, h: 8, rot: -61.6, color: '#6F6FD4' },
  { left: 254, top: 513, w: 5.3, h: 8, rot: 121.1, color: '#FF9EB8' },
  { left: 43, top: 384, w: 3, h: 8, rot: -103.7, color: '#FF9EB8' },
  { left: 76, top: 504, w: 5.6, h: 8, rot: 106.7, color: '#DBF7F8' },
  { left: 335, top: 259, w: 3, h: 8, rot: 40.2, color: '#DBF7F8' },
  { left: 311, top: 524, w: 6.3, h: 8.1, rot: 40.2, color: '#D32640' },
  { left: 43, top: 419, w: 6.6, h: 8, rot: 140.8, color: '#D32640' },
  { left: 202, top: 492, w: 3, h: 8, rot: 40.2, color: '#DBF7F8' },
];

export default function CheckInCompletedPage() {
  const { track } = useAnalytics();

  useEffect(() => {
    track('check_in_completed', {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{
        background: 'linear-gradient(to bottom, #00A7C8 21.1%, #FFFFFF 92.1%)',
      }}
    >
      <CheckInHeader />

      {/* Confetti layer — fades in as one unit */}
      <div
        className="absolute inset-0 w-full max-w-[430px] mx-auto pointer-events-none"
        //initial={{ opacity: 0 }}
        //animate={{ opacity: 1 }}
        //transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        {confettiParticles.map((p, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              left: `${(p.left / FRAME_W) * 100}%`,
              top: `${(p.top / FRAME_H) * 100}%`,
              width: `${(p.w / FRAME_W) * 100}%`,
              height: `${(p.h / FRAME_W) * 50}%`,
              backgroundColor: p.color,
              transform: `rotate(${p.rot}deg)`,
            }}
          />
        ))}
      </div>

      <motion.div
        className="flex-1 flex flex-col items-center justify-center px-6 relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Cloud character */}
        <motion.img
          src={hopefulCloudAsset}
          alt="Happy cloud"
          className="w-[208px] min-[414px]:w-[260px] max-w-full mb-6"
          variants={itemVariants}
        />

        {/* "You did it!" */}
        <motion.h2
          className="text-[#1B2831] font-bold text-[32px] md:text-[34px] lg:text-[36px] text-center mt-16"
          style={{ fontFamily: 'DM Sans', letterSpacing: '-1.28px' }}
          variants={itemVariants}
        >
          You did it!
        </motion.h2>

        {/* Subtitle */}
        <motion.p
          className="text-[#1B2831] font-semibold text-2xl md:text-[26px] lg:text-[28px] text-center mt-2 max-w-[344px]"
          style={{ fontFamily: 'DM Sans', letterSpacing: '-0.96px' }}
          variants={itemVariants}
        >
          Your cloud feels better now
        </motion.p>

        {/* Message */}
        <motion.p
          className="text-base md:text-[18px] lg:text-[20px] text-[#1B2831] text-center mt-4 max-w-[344px]"
          style={{ fontFamily: 'DM Sans' }}
          variants={itemVariants}
        >
          Remember, thoughts are like clouds: They always come and go.
        </motion.p>
      </motion.div>
    </div>
  );
}
